/**
 * API de Accesos (`/api/v1/accesos`): quién puede entrar a DEENEX Activa por mail.
 *
 * Va por apiClient, no por el `req()` de Activa: estas rutas piden el token de PERSONA (el del login
 * por mail), que es el que lleva apiClient, y si venció lo renueva con /auth/refresh. El token de
 * Activa (el de un comercio) no entra acá.
 *
 * Los errores del backend llegan como `{ success: false, error, code }`: se relanzan como un Error con
 * ese texto, más `status` y `code`, para que la página muestre lo que dijo el backend.
 */

import { apiClient } from "../../services/api/client";
import { listaDeMarcas, type ListaMarcas } from "../campana-nueva/api";

/**
 * Qué es cada persona: «equipo» (Deenex: entra a todos los comercios y hace todo) o «comercio»
 * (alguien de un cliente: entra sólo a sus `comercios`, ve lo suyo, contesta conversaciones y maneja
 * las bajas). Un dueño es siempre del equipo.
 */
export type RolAcceso = "equipo" | "comercio";

export type PersonaAcceso = {
  id: string;
  email: string;
  nombre: string;
  estado: "activo" | "revocado";
  /** El mail de quien la sumó, o "ACTIVA_DUENOS" si la sumó la configuración. */
  agregadoPor: string;
  agregadoEn: string | null;
  revocadoPor: string | null;
  revocadoEn: string | null;
  ultimoIngresoEn: string | null;
  esVos: boolean;
  esDueno: boolean;
  /** Puede faltar en un backend de antes de los roles: ahí todos eran del equipo. */
  rol?: RolAcceso;
  /** Los comercios de una persona de un cliente; vacía para el equipo. */
  comercios?: string[];
};

export type ListaAccesos = {
  personas: PersonaAcceso[];
  soyDueno: boolean;
  duenos: { email: string; nombre: string }[];
  smtpConfigurado: boolean;
  claveCompartidaActiva: boolean;
  urlLogin: string;
};

/** Un error de la API de Accesos: el texto del backend, con su status y su código. */
export type ErrorAccesos = Error & { status?: number; code?: string };

function relanzar(err: any): never {
  const data = err?.response?.data;
  throw Object.assign(
    new Error(data?.error || "No pudimos completar la operación. Probá de nuevo en un rato."),
    { status: err?.response?.status as number | undefined, code: data?.code as string | undefined },
  ) as ErrorAccesos;
}

async function pedir<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  try {
    return (await p).data.data;
  } catch (err) {
    return relanzar(err);
  }
}

export const accesosApi = {
  /** La lista para la página, con quién es dueño, si hay SMTP y si sigue la clave del equipo. */
  listar: () => pedir<ListaAccesos>(apiClient.get("/accesos")),

  /** Los comercios, para el selector de quien entró por mail (la misma forma que con la clave). */
  marcas: async (): Promise<ListaMarcas> => listaDeMarcas(await pedir<unknown>(apiClient.get("/accesos/marcas"))),

  /**
   * Suma a una persona (o le devuelve el acceso si lo tenía quitado), del equipo o de un cliente con
   * sus comercios. Sólo dueños.
   */
  agregar: (datos: { email: string; nombre?: string; rol: RolAcceso; comercios?: string[] }) =>
    pedir<{ persona: PersonaAcceso; avisoEnviado: boolean; comerciosSinVerificar?: string[] }>(apiClient.post("/accesos", datos)),

  /**
   * Cambia el nombre, el rol o los comercios (lo que venga). Sólo dueños. Un comercio que no existe es 422
   * COMERCIO_INEXISTENTE; `comerciosSinVerificar`: los que no se pudieron confirmar porque la plataforma no respondió
   * (se aceptan y la página lo avisa).
   */
  editar: (id: string, datos: { nombre?: string; rol?: RolAcceso; comercios?: string[] }) =>
    pedir<{ persona: PersonaAcceso; comerciosSinVerificar?: string[] }>(apiClient.patch(`/accesos/${encodeURIComponent(id)}`, datos)),

  /** Le quita el acceso: deja de poder entrar y se le cierran las sesiones. Sólo dueños. */
  revocar: (id: string) =>
    pedir<{ persona: PersonaAcceso }>(apiClient.post(`/accesos/${encodeURIComponent(id)}/revocar`)),

  /** Le devuelve el acceso. Sólo dueños. */
  reactivar: (id: string) =>
    pedir<{ persona: PersonaAcceso; avisoEnviado: boolean }>(apiClient.post(`/accesos/${encodeURIComponent(id)}/reactivar`)),

  /**
   * Borra los códigos equivocados y los códigos pedidos de sus últimas 24 h: puede volver a pedir uno
   * desde cualquier navegador. Sólo dueños.
   */
  desbloquear: (id: string) =>
    pedir<{ persona: PersonaAcceso }>(apiClient.post(`/accesos/${encodeURIComponent(id)}/desbloquear`)),
};
